<template>
  <div class="organization-dashboard">
    <div class="metrics-grid">
      <OrganizationalMetric
        v-for="metric in metrics"
        :key="metric.id"
        :count="metric.count"
        :value="metric.value"
        :draft-count="metric.draftCount"
        :guidance-text="metric.guidance"
        @add="$emit('add', metric.id)"
        @draft="$emit('draft', metric.id)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import OrganizationalMetric from '@components/organizations/OrganizationalMetric/OrganizationalMetric.vue'
import { useSidebarCountStore } from '@/store/pinia/sidebarCount'
import { isProductReadinessEnabled } from '@/utils/accountProductFlags'
import { LD_FLAG_IDEA_MDP_622 } from '@components/constants/launchDarklyFlagKeys'
import launchDarklyService from '@/services/launchdarkly'

// Initialize the sidebar count store
const sidebarCountStore = useSidebarCountStore()

// Props definition
const props = defineProps({
  title: {
    type: String,
    default: 'Organization Dashboard Section'
  }
})

const emit = defineEmits(['add', 'draft'])

// Former "I'd like to:" banner prerequisite sentences (Rules page / ER-12263),
// reused as the "+ Add" guidance tooltip when a tile's Active count is 0.
const GUIDANCE_TEXT = {
  employees: 'To assign an Employee to a Position, you need to have at least one Active Employee',
  positions: 'To assign an Employee to a Position, you need to have at least one Active Position',
  roles: 'To assign a Role to an Employee, you need to have at least one Active Role',
  responsibilities:
    'To assign a Responsibility to an Employee, you need to have at least one Active Responsibility',
  departments:
    'To assign a Position to a Department, you need to have at least one Active Department',
}

// ER-12266 / idea-MDP-622 — Active Departments tile is always shown regardless of
// the Readiness product. Kill switch: starts visible and hides only when the flag
// explicitly resolves false (avoids a flash in the normal, flag-unset case).
const showDepartmentsTile = ref(true)
onMounted(async () => {
  try {
    showDepartmentsTile.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_622, true)) !== false
  } catch (error) {
    console.error('Failed to resolve Departments tile flag:', error)
    showDepartmentsTile.value = true
  }
})

// Compute metrics from store
const metrics = computed(() => {
  const items = [
    {
      id: 'employees',
      count: 'Active Employees',
      value: sidebarCountStore.getEmployeesCount,
      draftCount: sidebarCountStore.getEmployeesDraftCount,
      guidance: GUIDANCE_TEXT.employees,
    },
    {
      id: 'positions',
      count: 'Active Positions',
      value: sidebarCountStore.getPositionsCount,
      draftCount: sidebarCountStore.getPositionsDraftCount,
      guidance: GUIDANCE_TEXT.positions,
    },
  ]
  if (isProductReadinessEnabled()) {
    items.push(
      {
        id: 'roles',
        count: 'Active Roles',
        value: sidebarCountStore.getRolesCount,
        draftCount: sidebarCountStore.getRolesDraftCount,
        guidance: GUIDANCE_TEXT.roles,
      },
      {
        id: 'responsibilities',
        count: 'Active Responsibilities',
        value: sidebarCountStore.getResponsibilitiesCount,
        draftCount: sidebarCountStore.getResponsibilitiesDraftCount,
        guidance: GUIDANCE_TEXT.responsibilities,
      }
    )
  }
  if (showDepartmentsTile.value) {
    items.push({
      id: 'departments',
      count: 'Active Departments',
      value: sidebarCountStore.getDepartmentsCount,
      draftCount: sidebarCountStore.getDepartmentsDraftCount,
      guidance: GUIDANCE_TEXT.departments,
    })
  }
  return items
})
</script>

<style scoped>

.dashboard-title {
  color: #7C3AED;
  font-family: Inter, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  margin: 0;
  position: relative;
}

.dashboard-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 100%;
  height: 3px;
  background: #7C3AED;
  border-radius: 2px;
}

.metrics-grid {
  display: grid;
  /* ER-12266: column count follows the number of tiles (up to five). The Figma
     tile minimum is 180px, so five tiles fit the 1086px dashboard row without
     overflow (5 * 180 + 4 * 24 = 996 < 1086) and auto-fit collapses to the tile
     count when Readiness is off (3 tiles) or the flag hides Departments (2/4). */
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  width: 100%;
}

@media (max-width: 767px) {
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>
